import React, {useState, useEffect} from 'react';
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    Alert,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Snackbar,
    Tooltip,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TablePagination,
    Divider
} from '@mui/material';
import {
    getAllVectorPoints,
    addVectorPoint,
    deleteVectorPoints,
    upsertVectorPoint,
    getAllByTableNameAndRowId
} from "../api/AiAgentApi";
import AuditLogFeed from "./AuditLogFeed";
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StorageIcon from '@mui/icons-material/Storage';

export default function VectorStoragePage() {
    const [points, setPoints] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentPointId, setCurrentPointId] = useState(null);
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'success'});
    const [deleteDialog, setDeleteDialog] = useState({open: false, pointId: null});
    const [auditLogs, setAuditLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    
    const rowsPerPage = 150;

    const [pointData, setPointData] = useState({
        name: '',
        text: '',
        id: '' // Only for edit
    });

    useEffect(() => {
        loadPoints();
    }, []);

    const loadPoints = async () => {
        try {
            setLoading(true);
            const data = await getAllVectorPoints(0, rowsPerPage);
            setPoints(data);
            setError(null);
        } catch (err) {
            setError('Не удалось загрузить данные из векторного хранилища.');
            console.error('Error loading vector points:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredPoints = points.filter(point => {
        const content = point.payload?.doc_content || point.payload?.text || '';
        const name = point.payload?.name || '';
        const search = searchQuery.toLowerCase();
        return content.toLowerCase().includes(search) || name.toLowerCase().includes(search);
    });

    const handleOpenAddDialog = () => {
        setIsEditMode(false);
        setPointData({
            name: '',
            text: '',
            id: ''
        });
        setOpenDialog(true);
    };

    const handleOpenEditDialog = async (point) => {
        setIsEditMode(true);
        setCurrentPointId(point.id);
        setPointData({
            name: point.payload?.name || '',
            text: point.payload?.doc_content || point.payload?.text || '',
            id: point.id
        });
        setOpenDialog(true);
        
        // Fetch audit logs
        try {
            setLogsLoading(true);
            const logs = await getAllByTableNameAndRowId('vector_storage', point.id);
            setAuditLogs(logs || []);
        } catch (err) {
            console.error('Error loading audit logs:', err);
            setAuditLogs([]);
        } finally {
            setLogsLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setIsEditMode(false);
        setCurrentPointId(null);
        setAuditLogs([]);
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setPointData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            if (!pointData.text.trim()) {
                setSnackbar({
                    open: true,
                    message: 'Пожалуйста, введите текст',
                    severity: 'error'
                });
                return;
            }

            if (isEditMode) {
                await upsertVectorPoint({
                    id: currentPointId,
                    name: pointData.name,
                    text: pointData.text
                });
                setSnackbar({
                    open: true,
                    message: 'Документ успешно обновлен!',
                    severity: 'success'
                });
            } else {
                await addVectorPoint({
                    name: pointData.name,
                    text: pointData.text
                });
                setSnackbar({
                    open: true,
                    message: 'Документ успешно добавлен!',
                    severity: 'success'
                });
            }

            handleCloseDialog();
            await loadPoints();
        } catch (err) {
            console.error('Error saving vector point:', err);
            setSnackbar({
                open: true,
                message: 'Ошибка при сохранении. Пожалуйста, попробуйте еще раз.',
                severity: 'error'
            });
        }
    };

    const handleOpenDeleteDialog = (pointId) => {
        setDeleteDialog({
            open: true,
            pointId
        });
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            pointId: null
        });
    };

    const handleDeletePoint = async () => {
        try {
            await deleteVectorPoints([deleteDialog.pointId]);
            handleCloseDeleteDialog();
            setSnackbar({
                open: true,
                message: 'Документ успешно удален!',
                severity: 'success'
            });
            await loadPoints();
        } catch (err) {
            console.error('Error deleting vector point:', err);
            setSnackbar({
                open: true,
                message: 'Ошибка при удалении. Пожалуйста, попробуйте еще раз.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({...snackbar, open: false});
    };

    return (
        <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
            <Box sx={{mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        База знаний
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Управление документами для обучения AI
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Поиск по документам..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 300 }}
                    />
                    <Tooltip title="Обновить список">
                        <IconButton onClick={loadPoints} color="primary">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Добавить
                    </Button>
                </Stack>
            </Box>

            {error && (
                <Alert severity="error" sx={{mb: 3}}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress/>
                </Box>
            ) : filteredPoints.length === 0 ? (
                <Paper sx={{p: 6, textAlign: 'center'}}>
                    <StorageIcon sx={{fontSize: 60, color: 'text.secondary', mb: 2}} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {searchQuery ? 'Ничего не найдено' : 'Документы не найдены'}
                    </Typography>
                    {!searchQuery && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenAddDialog}
                            sx={{mt: 2}}
                        >
                            Добавить документ
                        </Button>
                    )}
                </Paper>
            ) : (
                <Paper sx={{width: '100%', overflow: 'hidden'}}>
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 200 }}>Название</TableCell>
                                    <TableCell>Текст (Payload)</TableCell>
                                    <TableCell align="center" sx={{ width: 120 }}>Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredPoints.map((point) => {
                                    const title = point.payload?.name || '-';
                                    const content = point.payload?.doc_content || point.payload?.text || '';
                                    return (
                                        <TableRow key={point.id} hover>
                                            <TableCell sx={{ fontWeight: 'medium', verticalAlign: 'top' }}>
                                                {title}
                                            </TableCell>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 800,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                                    {content}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                                                <PointActionsMenu
                                                    point={point}
                                                    onEdit={handleOpenEditDialog}
                                                    onDelete={handleOpenDeleteDialog}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isEditMode ? 'Редактировать документ' : 'Добавить новый документ'}
                </DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{mt: 2}}>
                        <TextField
                            fullWidth
                            label="Название документа"
                            name="name"
                            value={pointData.name}
                            onChange={handleInputChange}
                            margin="normal"
                            placeholder="Например: Адрес"
                            helperText="Короткое название для идентификации документа"
                        />
                        <TextField
                            fullWidth
                            label="Текст документа"
                            name="text"
                            value={pointData.text}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            multiline
                            rows={6}
                            helperText="Этот текст будет использоваться AI для ответов пользователям"
                        />
                    </Box>

                    {isEditMode && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                История изменений
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {logsLoading ? (
                                <Box display="flex" justifyContent="center" py={2}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <AuditLogFeed logs={auditLogs} />
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Отмена</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {isEditMode ? 'Сохранить' : 'Добавить'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Подтверждение удаления</DialogTitle>
                <DialogContent>
                    <Typography>
                        Вы уверены, что хотите удалить этот документ?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Отмена</Button>
                    <Button
                        onClick={handleDeletePoint}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Удалить
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

function PointActionsMenu({ point, onEdit, onDelete }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        onEdit(point);
        handleClose();
    };

    const handleDelete = () => {
        onDelete(point.id);
        handleClose();
    };

    return (
        <>
            <IconButton onClick={handleClick} size="small">
                <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleEdit}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Редактировать</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Удалить</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
